import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@amazing-care/ui/breadcrumb";
import { Link, useLocation } from "react-router";
import { useActivity } from "@/features/activities/use-activities";
import { useDocument } from "@/features/documents/use-documents";
import { ROUTES } from "@/routes";
import { useDepartments } from "./use-departments";

interface Crumb {
  label: string;
  href?: string;
}

export function StaffBreadcrumbs() {
  const { pathname } = useLocation();
  const { data: departments } = useDepartments();
  const segments = pathname.split("/").filter(Boolean);
  // The same queries the page itself runs, so naming the crumb costs nothing.
  const documentId =
    segments[1] === "departments" && segments[3] === "documents" ? segments[4] : "";
  const activityId = segments[1] === "activities" ? segments[2] : "";
  const { data: document } = useDocument(documentId ?? "", { enabled: !!documentId });
  const { data: activity } = useActivity(activityId ?? "", { enabled: !!activityId });
  const crumbs: Crumb[] = [];

  if (segments[0] !== "staff") {
    crumbs.push({ label: "AmazingCare" });
  } else {
    crumbs.push({ label: "AmazingCare", href: ROUTES.staff });

    if (segments[1] === "departments" && segments[2]) {
      const department = departments?.find((item) => item.id === segments[2]);
      crumbs.push({
        label: department?.name ?? "Department",
        href: ROUTES.department(segments[2]),
      });

      if (documentId) {
        crumbs.push({ label: document?.name ?? "Document" });
      }
    } else if (activityId) {
      crumbs.push({ label: activity?.title ?? "Activity" });
    } else if (segments[1] === "admin" && segments[2] === "users") {
      crumbs.push({ label: "User management" });
    } else if (segments[1] === "admin" && segments[2] === "content") {
      crumbs.push({ label: "Content management" });
    }
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <span key={crumb.href ?? crumb.label} className="inline-flex items-center gap-1.5">
              <BreadcrumbItem>
                {isLast || !crumb.href ? (
                  <BreadcrumbPage className="text-sm font-medium">{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink render={<Link to={crumb.href} />}>{crumb.label}</BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </span>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
